import React, { useState } from 'react';
import { fetchScreeningSnapshots, getDebugScreenshotFileUrl } from '../api/client';
import { NEU_BG, TEXT_SECONDARY, TEXT_TERTIARY } from '../theme';

/**
 * The two per-screening popups — "נסגר" (the final-scan timeline) and
 * "לא נסרק" (why the final scan never ran) — shared by MovieDetail and the
 * branch page so both explain a screening the same way.
 */

export const CHAIN_EXPECTED_SCANS = {
  'Cinema City': 6, 'Planet': 13, 'Rav-Chen': 13,
  'Hot Cinema': 21, 'Movieland': 21, 'Lev': 21, 'Cinematheque TLV': 21, 'Cinematheque Herzliya': 21,
  'Cinematheque Holon': 21, 'Cinematheque Haifa': 21, 'Cinematheque Rosh Pina': 21, 'Cinematheque Sderot': 21,
};

export const LANGUAGE_LABELS = {
  dubbed_hebrew: 'מדובב לעברית',
  dubbed_arabic: 'מדובב לערבית',
  dubbed_russian: 'מדובב לרוסית',
  dubbed_french: 'מדובב לצרפתית',
  dubbed_spanish: 'מדובב לספרדית',
  dubbed_english: 'מדובב לאנגלית',
  dubbed: 'מדובב לעברית',
  subtitled_hebrew: 'כתוביות בעברית',
  subtitled_arabic: 'כתוביות בערבית',
  subtitled_russian: 'כתוביות ברוסית',
  subtitled_french: 'כתוביות בצרפתית',
  subtitled_spanish: 'כתוביות בספרדית',
  subtitled_english: 'כתוביות באנגלית',
  subtitled: 'כתוביות',
  hebrew: 'עברית',
  english: 'אנגלית',
  russian: 'רוסית',
};

export function SnapshotModal({ modal: snapshotModal, onClose }) {
  if (!snapshotModal) return null;
  return (

        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => onClose()}>
          <div style={{
            background: NEU_BG, borderRadius: 14, padding: '24px 28px', minWidth: 340, maxWidth: 480,
            maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.4)', position: 'relative',
            direction: 'rtl',
          }} onClick={e => e.stopPropagation()}>
            <button onClick={() => onClose()} style={{
              position: 'absolute', top: 12, right: 16, background: 'none', border: 'none',
              fontSize: 20, cursor: 'pointer', color: TEXT_TERTIARY, lineHeight: 1,
            }}>✕</button>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              סריקות סופיות — {snapshotModal.screening.cinema}
            </div>
            <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 16 }}>
              {snapshotModal.chain} | {snapshotModal.screening.showtime
                ? new Date(snapshotModal.screening.showtime).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                : ''}
            </div>
            {snapshotModal.loading ? (
              <div style={{ color: TEXT_TERTIARY, fontSize: 13 }}>טוען...</div>
            ) : snapshotModal.error ? (
              <div style={{ color: 'var(--danger)', fontSize: 13 }}>{snapshotModal.error}</div>
            ) : (snapshotModal.snapshots || []).length === 0 ? (
              <div>
                <div style={{ color: TEXT_TERTIARY, fontSize: 13, marginBottom: 8 }}>
                  אין נתוני סריקות שמורים (הקרנה נסרקה לפני עדכון המערכת)
                </div>
                {(snapshotModal.screening.tickets_sold > 0 || snapshotModal.screening.total_seats > 0) && (
                  <div style={{ fontSize: 14, color: TEXT_SECONDARY }}>
                    {'מצב סופי: '}<strong style={{ color: 'var(--warn)' }}>{snapshotModal.screening.tickets_sold}</strong>
                    {' / '}{snapshotModal.screening.total_seats}{' כרטיסים'}
                    {snapshotModal.screening.total_seats > 0 && (
                      <span style={{ color: TEXT_TERTIARY }}>{' '}({Math.round(snapshotModal.screening.tickets_sold * 100 / snapshotModal.screening.total_seats)}%)</span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ color: TEXT_TERTIARY, fontSize: 11 }}>
                      <th style={{ textAlign: 'right', paddingBottom: 6, fontWeight: 500 }}>שעה</th>
                      <th style={{ textAlign: 'center', paddingBottom: 6, fontWeight: 500 }}>נמכרו</th>
                      <th style={{ textAlign: 'center', paddingBottom: 6, fontWeight: 500 }}>סה״כ</th>
                      <th style={{ textAlign: 'center', paddingBottom: 6, fontWeight: 500 }}>תפוסה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(snapshotModal.snapshots || []).map((s, i) => {
                      const occ = s.total_seats ? Math.round(s.tickets_sold * 100 / s.total_seats) : 0;
                      return (
                        <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '5px 0' }}>
                            {s.recorded_at ? new Date(s.recorded_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--warn)' }}>{s.tickets_sold ?? '-'}</td>
                          <td style={{ textAlign: 'center', color: TEXT_SECONDARY }}>{s.total_seats ?? '-'}</td>
                          <td style={{ textAlign: 'center', color: occ > 75 ? 'var(--danger)' : occ > 40 ? 'var(--warn)' : 'var(--ok)' }}>{occ}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 12, fontSize: 12, color: TEXT_TERTIARY, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    {(snapshotModal.snapshots || []).length} מתוך {CHAIN_EXPECTED_SCANS[snapshotModal.chain] || '?'} סריקות בוצעו
                  </span>
                  {snapshotModal.screening.final_screenshot && (
                    <a
                      href={getDebugScreenshotFileUrl(snapshotModal.screening.final_screenshot)}
                      target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--accent)', fontSize: 12 }}
                    >צילום מסך אחרון ↗</a>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      
  );
}

export function NotScannedModal({ screening, onClose }) {
  if (!screening) return null;
  return (

        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => onClose()}>
          <div style={{
            background: NEU_BG, borderRadius: 14, padding: '24px 28px', minWidth: 300, maxWidth: 420,
            boxShadow: '0 8px 40px rgba(0,0,0,0.4)', position: 'relative', direction: 'rtl',
          }} onClick={e => e.stopPropagation()}>
            <button onClick={() => onClose()} style={{
              position: 'absolute', top: 12, right: 16, background: 'none', border: 'none',
              fontSize: 20, cursor: 'pointer', color: TEXT_TERTIARY, lineHeight: 1,
            }}>✕</button>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)', marginBottom: 4 }}>לא נסרק סופית</div>
            <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginBottom: 16 }}>
              {screening.cinema}
              {screening.showtime ? ' | ' + new Date(screening.showtime).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
            <div style={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.6 }}>
              {!screening.has_booking_url
                ? '❌ אין booking URL — הסריקה לא יכולה להתחיל ללא כתובת ייעודית'
                : screening.captcha_blocked_at
                  ? `🔒 רקאפצ'ה חסמה גישה בשעה ${new Date(screening.captcha_blocked_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`
                  : screening.booking_url_stale_at
                    ? `⚠️ כתובת הדף פגה (booking URL stale) בשעה ${new Date(screening.booking_url_stale_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} — ייתכן שהטוקן פג`
                    : screening.verification_mismatch_at
                      ? `⚠️ דף הכרטיסים לא תאם את הסרט/סניף/שעה בשעה ${new Date(screening.verification_mismatch_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`
                      : '❓ הסריקה הסופית לא הורצה — ייתכן שהמתזמן לא הגיע לחלון הסגירה'
              }
            </div>
          </div>
        </div>
      
  );
}

/** State + openers for both popups; render `modals` once in the page. */
export function useScreeningModals() {
  const [snapshotModal, setSnapshotModal] = useState(null);
  const [notScanned, setNotScanned] = useState(null);
  const openSnapshot = async (screening) => {
    setSnapshotModal({ screening, snapshots: null, loading: true });
    try {
      const data = await fetchScreeningSnapshots(screening.id);
      setSnapshotModal({ screening, ...data, loading: false });
    } catch {
      setSnapshotModal({ screening, snapshots: [], loading: false, error: 'שגיאה בטעינת נתונים' });
    }
  };
  const modals = (
    <>
      <SnapshotModal modal={snapshotModal} onClose={() => setSnapshotModal(null)} />
      <NotScannedModal screening={notScanned} onClose={() => setNotScanned(null)} />
    </>
  );
  return { openSnapshot, openNotScanned: setNotScanned, modals };
}
